import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalFonts,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  AvatarStack,
  Mono,
  ScreenFrame,
  SignalButton,
  Waveform,
} from '@/common/components/signal';
import { boards } from '../fixtures';

/**
 * 07 — Boards list.
 *
 * A gallery of kanban views. Each card is topped by a 4px rail in the board's
 * own accent, which is the only thing distinguishing one board from another at
 * a glance — the titles are all similar-length text, so the color does the
 * identifying work. The waveform is dimmed to 75%: it belongs to the projects
 * inside the board, not to the board itself, and shouldn't compete with the
 * rail.
 */
export const Screen07Boards: React.FC = () => (
  <ScreenFrame
    index="07"
    title="Boards list"
    frameSx={{ padding: '26px 26px 32px' }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        marginBottom: '22px',
      }}
    >
      <Box>
        <Box
          component="h3"
          sx={{
            margin: 0,
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '34px',
            letterSpacing: '-0.03em',
            lineHeight: 1,
          }}
        >
          Boards
        </Box>
        <Box
          component="p"
          sx={{
            margin: '6px 0 0',
            fontSize: '13px',
            color: signalText.secondary,
          }}
        >
          Kanban views over your projects — releases, remix packs, licensing
          queues.
        </Box>
      </Box>

      <SignalButton sx={{ padding: '10px 17px' }}>＋ Create board</SignalButton>
    </Box>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr) 240px',
        gap: '16px',
        alignItems: 'stretch',
      }}
    >
      {boards.map((board) => (
        <Box
          key={board.name}
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.cardGradient,
            overflow: 'hidden',
            boxShadow: signalShadows.cardSoft,
            transition: 'border-color 120ms ease',
            '&:hover': { borderColor: signalBorders.accentHover },
          }}
        >
          <Box sx={{ height: 4, background: board.accent }} />

          <Box sx={{ padding: '16px 16px 14px' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontFamily: signalFonts.display,
                    fontWeight: 700,
                    fontSize: '17px',
                    letterSpacing: '-0.015em',
                    lineHeight: 1.2,
                  }}
                >
                  {board.name}
                </Box>
                <Mono size={10.5} sx={{ display: 'block', marginTop: '5px' }}>
                  {board.meta}
                </Mono>
              </Box>
              <Box
                component="span"
                sx={{ color: signalText.faint, fontSize: '13px' }}
              >
                ⋮
              </Box>
            </Box>

            <Waveform
              bars={board.wave}
              height={30}
              sx={{ margin: '14px 0 12px', opacity: 0.75 }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: `1px solid ${signalBorders.divider}`,
              }}
            >
              <AvatarStack
                people={board.people}
                size={22}
                overlap={7}
                ring={signalSurfaces.avatarRing}
              />
              <Mono size={10.5}>{board.updated}</Mono>
            </Box>
          </Box>
        </Box>
      ))}

      <Box
        sx={{
          border: `1px dashed ${signalBorders.dashed}`,
          borderRadius: `${signalRadii['2xl']}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          color: signalText.muted,
          padding: '20px',
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            color: signalText.primary,
            borderColor: 'rgba(255,255,255,0.28)',
          },
        }}
      >
        <Box component="span" sx={{ fontSize: '20px' }}>
          ＋
        </Box>
        <Box component="span" sx={{ fontSize: '12.5px', lineHeight: 1.5 }}>
          New board
          <br />
          <Box component="span" sx={{ color: signalText.faint }}>
            Seeds 4 default columns
          </Box>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen07Boards;
